import { Transition } from "@headlessui/react";
import { useFoodJournal } from "../JournalingProvider";
import { FileIcon, NotebookTextIcon } from "../fancyIcons";
import useLabs from "../hooks/useLabs";
import { useIdentity } from "../../../provider-portal/IdentityProvider";

export const JournalConversationPreview = ({
  journalEntryId,
}: {
  journalEntryId: string;
}) => {
  const { useGetJournalEntry } = useFoodJournal();

  const { isLoading, data } = useGetJournalEntry(journalEntryId);

  return (
    <div className="tw-flex tw-items-center tw-gap-1">
      <NotebookTextIcon className="tw-size-4 tw-shrink-0 tw-fill-neutral-800 tw-stroke-neutral-500" />
      <div className="tw-overflow-hidden">
        <Transition
          as="div"
          show={!isLoading}
          enter="tw-transform tw-transition tw-duration-[400ms]"
          enterFrom="tw-opacity-0 tw--translate-x-full"
          enterTo="tw-opacity-100 tw-translate-x-0"
          leave="tw-transform tw-duration-200 tw-transition tw-ease-in-out"
          leaveFrom="tw-opacity-100 tw-translate-x-0"
          leaveTo="tw-opacity-0 tw--translate-x-full"
        >
          {data && (
            <div className="tw-truncate tw-text-neutral-800">
              {data.content !== "" ? data.content : "Journal entry"}
            </div>
          )}
        </Transition>
      </div>
    </div>
  );
};

export const LabConversationPreview = ({
  patientLabId,
  patientId,
}: {
  patientLabId: string;
  patientId?: string;
}) => {
  const { useGetLab } = useLabs();
  const { isProvider } = useIdentity();

  let queryPatientId = "me";
  if (isProvider && patientId) {
    queryPatientId = patientId;
  }
  const { isLoading, data } = useGetLab(queryPatientId, patientLabId);

  return (
    <div className="tw-flex tw-items-center tw-gap-1">
      <FileIcon className="tw-size-4 tw-shrink-0 tw-fill-neutral-800 tw-stroke-neutral-500" />
      <div className="tw-overflow-hidden">
        <Transition
          as="div"
          show={!isLoading}
          enter="tw-transform tw-transition tw-duration-[400ms]"
          enterFrom="tw-opacity-0 tw--translate-x-full"
          enterTo="tw-opacity-100 tw-translate-x-0"
          leave="tw-transform tw-duration-200 tw-transition tw-ease-in-out"
          leaveFrom="tw-opacity-100 tw-translate-x-0"
          leaveTo="tw-opacity-0 tw--translate-x-full"
        >
          {data && (
            <div className="tw-truncate tw-text-neutral-800">
              {data.title !== "" ? data.title : "Lab report"}
            </div>
          )}
        </Transition>
      </div>
    </div>
  );
};

export const TextMessagePreview = ({
  content,
  isHtml,
}: {
  content: string;
  isHtml: boolean;
}) => {
  if (isHtml) {
    const previewContent = extractTextPreviewContentFromHTML(content);
    if (!previewContent) {
      return (
        <div className="tw-truncate tw-italic tw-text-gray-500">
          Preview unavailable
        </div>
      );
    }
    if (previewContent.paraphrase) {
      return (
        <div className="tw-truncate tw-italic tw-text-gray-500">
          {previewContent.text}
        </div>
      );
    }
    return <div className="tw-truncate">{previewContent.text}</div>;
  } else {
    return <div className="tw-truncate">{content}</div>;
  }
};

// While previewing `html`, we should take the first `p` tag and display it as a preview.
// Or, get `a` tag and return "Sent a link" as a preview.
const extractTextPreviewContentFromHTML = (
  content: string,
): {
  paraphrase: boolean;
  text: string;
} | null => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, "text/html");
  const firstParagraph = doc.querySelector("p");
  if (firstParagraph?.textContent) {
    return {
      paraphrase: false,
      text: firstParagraph.textContent,
    };
  }

  const firstLink = doc.querySelector("a");
  if (firstLink) {
    return {
      paraphrase: true,
      text: "Sent a link",
    };
  }

  return null;
};
